export const TRANSCRIPTION_STATES = {
  ready: 'ready',
  textOnly: 'text_only',
  empty: 'empty',
  pending: 'pending',
  invalid: 'invalid',
};

const UNKNOWN_SPEAKER = 'Unknown';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const toMs = (value) => {
  const number = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(number) && number >= 0 ? number : null;
};

/**
 * Validates the raw call transcription (JSON string from the API) and maps it
 * into the shape the transcription dialog renders.
 *
 * Raw: { text, segments: [{ speaker, text, start_ms, end_ms }], speaker_durations_ms: { [speaker]: ms } }
 */
export class TranscriptionMapper {
  static map(rawTranscription, transcriptionStatus) {
    const data = TranscriptionMapper.parse(rawTranscription);

    if (data === null) {
      if (transcriptionStatus && transcriptionStatus !== 'completed') return TranscriptionMapper.result(TRANSCRIPTION_STATES.pending);
      return TranscriptionMapper.result(isNonEmptyString(rawTranscription) ? TRANSCRIPTION_STATES.invalid : TRANSCRIPTION_STATES.empty);
    }

    const text = isNonEmptyString(data.text) ? data.text.trim() : '';
    const segments = TranscriptionMapper.mapSegments(data.segments);

    if (segments.length === 0) {
      return TranscriptionMapper.result(text ? TRANSCRIPTION_STATES.textOnly : TRANSCRIPTION_STATES.empty, { text });
    }

    const speakers = TranscriptionMapper.mapSpeakers(segments, data.speaker_durations_ms);
    const speakerIndex = speakers.reduce((acc, speaker) => ({ ...acc, [speaker.name]: speaker.index }), {});

    const messages = TranscriptionMapper.mergeConsecutive(segments).map((message, id) => ({
      ...message,
      id,
      speakerIndex: speakerIndex[message.speaker],
    }));

    return TranscriptionMapper.result(TRANSCRIPTION_STATES.ready, { text, speakers, messages });
  }

  static parse(rawTranscription) {
    if (rawTranscription && typeof rawTranscription === 'object') return rawTranscription;
    if (!isNonEmptyString(rawTranscription)) return null;

    try {
      const parsed = JSON.parse(rawTranscription);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  static mapSegments(segments) {
    if (!Array.isArray(segments)) return [];

    return segments
      .filter((segment) => segment && typeof segment === 'object' && isNonEmptyString(segment.text))
      .map((segment) => ({
        speaker: isNonEmptyString(segment.speaker) ? segment.speaker.trim() : UNKNOWN_SPEAKER,
        text: segment.text.trim(),
        startMs: toMs(segment.start_ms),
        endMs: toMs(segment.end_ms),
      }));
  }

  static mapSpeakers(segments, durations) {
    const names = [...new Set(segments.map((segment) => segment.speaker))];
    const validDurations = durations && typeof durations === 'object' ? durations : {};

    return names.map((name, index) => ({
      name,
      index,
      durationMs: toMs(validDurations[name]),
    }));
  }

  static mergeConsecutive(segments) {
    return segments.reduce((acc, segment) => {
      const last = acc[acc.length - 1];

      if (last && last.speaker === segment.speaker) {
        last.text = `${last.text} ${segment.text}`;
        last.endMs = segment.endMs ?? last.endMs;
        return acc;
      }

      return [...acc, { ...segment }];
    }, []);
  }

  static formatTime(ms) {
    if (ms === null || ms === undefined) return '';

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');

    return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
  }

  static result(state, { text = '', speakers = [], messages = [] } = {}) {
    return { state, text, speakers, messages };
  }
}
